import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Input, Label, ErrorMessage } from '../../styles/GlobalStyles';
import { UserRole } from '../../types';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-color);
  padding: var(--spacing-md);
`;

const LoginCard = styled(Card)`
  max-width: 450px;
  width: 100%;
  padding: var(--spacing-xl);
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
  text-align: center;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: var(--spacing-xl);
  font-size: 0.875rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: var(--spacing-lg);
  font-size: 0.875rem;
  color: var(--text-secondary);

  a {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated as CLUB_ADMIN
  useEffect(() => {
    if (isAuthenticated && user?.role === UserRole.CLUB_ADMIN) {
      // Check if user has a clubId - if not, redirect to setup
      if (!user.profile.clubId) {
        navigate('/administration-club/setup', { replace: true });
      } else {
        navigate('/administration-club/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  // If already authenticated but not CLUB_ADMIN, show error
  if (isAuthenticated && user?.role !== UserRole.CLUB_ADMIN) {
    return (
      <Container>
        <LoginCard>
          <Title>Access Denied</Title>
          <ErrorMessage style={{ marginBottom: 'var(--spacing-lg)' }}>
            This portal is only accessible to club administrators.
          </ErrorMessage>
          <Button onClick={() => navigate('/dashboard')}>
            Go to Main Application
          </Button>
        </LoginCard>
      </Container>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      
      // The useEffect above will handle the redirect after login
    } catch (err: any) {
      // Check if the error is due to wrong role
      const errorCode = err.response?.data?.error?.code;
      
      if (errorCode === 'ROLE_NOT_AUTHORIZED' || errorCode === 'UNAUTHORIZED') {
        setError('This account does not have club administrator access.');
      } else {
        // Generic error message - don't reveal whether email or password was wrong
        setError('Invalid email or password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <LoginCard>
        <Title>Club Administration</Title>
        <Subtitle>Sign in to manage your club</Subtitle>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@club.com"
              required
              autoComplete="email"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </FormGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Button type="submit" disabled={isLoading || authLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </Form>

        <LinkText>
          Don't have an account?{' '}
          <Link to="/administration-club/register">Register your club</Link>
        </LinkText>
      </LoginCard>
    </Container>
  );
};
